/**
 * Constraint re-solve watcher (Faz 6.4) — same silent, history-free
 * pattern as dimAssociation: entity mutations (MOVE/grip/STRETCH/undo)
 * re-drive the affected constraint subsystems through the solver, bound
 * dimension edits feed their driving values, and constraints whose
 * entities disappeared are pruned. Solves triggered by the solver's own
 * write-backs are suppressed with the `solving` guard.
 *
 * Booted from the App bootstrap next to startDimAssociation.
 */

import { useAppStore } from '../../state/store';
import { useConstraintStore } from './constraintStore';
import { solveConstraintSet, syncBoundDimensions } from './solve';

let started = false;
let solving = false;

export function startConstraintWatcher(): void {
    if (started) return;
    started = true;

    // Geometry watcher: entity edits (user moves, undo/redo restores).
    useAppStore.subscribe((state, prev) => {
        if (solving) return;
        if (state.entities === prev.entities) return;
        const store = useConstraintStore.getState();
        if (store.set.constraints.length === 0) return;
        solving = true;
        try {
            // 1) Bound-dimension edits drive their constraint value
            //    (Properties panel: 500 -> 250 shortens the geometry).
            for (const [cid, dimId] of Object.entries(store.set.dimensions)) {
                const dim = state.entities[dimId];
                const prevDim = prev.entities[dimId];
                if (!dim || !prevDim || dim.data === prevDim.data) continue;
                if (dim.data.constraintDriven !== cid) continue;
                const measured = dim.data.measured;
                if (typeof measured === 'number' && Number.isFinite(measured)) {
                    const rec = store.set.constraints.find((c) => c.id === cid);
                    if (rec && Math.abs(measured - (rec.value ?? NaN)) > 1e-9) {
                        store.updateConstraint(cid, { driving: { literal: Math.abs(measured) }, value: Math.abs(measured) });
                    }
                }
            }
            // 2) Prune constraints whose entities are gone (delete paths
            //    outside undoableDelete keep the invariant anyway).
            const dangling = store.set.constraints
                .filter((c) => c.refs.some((r) => !state.entities[r.entityId]))
                .map((c) => c.id);
            if (dangling.length > 0) {
                useConstraintStore.getState().removeConstraints(dangling);
            }
            // 3) Re-solve and mirror solved values into bound dimensions.
            const result = solveConstraintSet();
            if (result.converged || result.conflicts.length > 0) syncBoundDimensions();
        } finally {
            solving = false;
        }
    });

    // Set watcher: parameter-table / driving-value / enabled-flag edits
    // re-solve even when no entity changed (e.g. PARAMETERS dialog edits).
    useConstraintStore.subscribe((state, prev) => {
        if (solving) return;
        if (state.set === prev.set) return;
        const paramsChanged = state.set.parameters !== prev.set.parameters;
        const prevById = new Map(prev.set.constraints.map((c) => [c.id, c]));
        const relevant = state.set.constraints.some((c) => {
            const p = prevById.get(c.id);
            if (!p) return false; // new records solve through their command
            return c.driving !== p.driving || c.enabled !== p.enabled;
        });
        if (!paramsChanged && !relevant) return;
        solving = true;
        try {
            const result = solveConstraintSet();
            if (result.converged || result.conflicts.length > 0) syncBoundDimensions();
        } finally {
            solving = false;
        }
    });
}

/** Guard for code paths that batch their own silent store writes. */
export const isConstraintSolving = (): boolean => solving;

/** Batch silent constraint-solver writes (watcher suppression). */
export function runWithoutConstraintWatcher<T>(fn: () => T): T {
    solving = true;
    try {
        return fn();
    } finally {
        solving = false;
    }
}
